import React, { useEffect, useMemo, useState } from "react";
import { getStores } from "../../api/productApi";

const CACHE_KEY = "sq_cached_stores_v1";

export default function StoreSelection({ onSelect = () => { } }) {
    const [stores, setStores] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

    // Load cached stores instantly (for faster UI)
    useEffect(() => {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setStores(parsed);
                    setLoading(false);
                }
            } catch {
                // ignore invalid cache
            }
        }
    }, []);

    // Fetch fresh stores in the background
    const fetchStores = async () => {
        try {
            setError("");
            if (stores.length === 0) setLoading(true);

            const data = await getStores({ token });
            const list = Array.isArray(data?.data) ? data.data : [];

            setStores(list);
            localStorage.setItem(CACHE_KEY, JSON.stringify(list));
        } catch (e) {
            setError("Failed to load stores. Please refresh the page.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredStores = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return stores;

        return stores.filter((s) => {
            const text = `${s?.name || ""} ${s?.location || ""} ${s?.id || ""} ${s?.status || ""
                }`.toLowerCase();

            return text.includes(q);
        });
    }, [stores, search]);

    return (
        <div className="min-h-screen flex items-start justify-center p-8">
            <div className="w-full max-w-2xl">
                <h1 className="text-4xl font-semibold text-center mb-8">
                    Please Select the Store
                </h1>

                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search store"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                    />
                </div>

                {loading && stores.length === 0 && (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="w-full border rounded-lg p-4 bg-white animate-pulse"
                            >
                                <div className="h-5 w-40 bg-gray-200 rounded mb-2" />
                                <div className="h-4 w-64 bg-gray-200 rounded" />
                            </div>
                        ))}
                    </div>
                )}

                {error && <div className="text-center text-red-500 py-4">{error}</div>}

                {!loading && !error && filteredStores.length === 0 && (
                    <div className="text-center text-gray-500 py-6">No stores found.</div>
                )}

                <div className="space-y-3">
                    {filteredStores.map((s, idx) => (
                        <button
                            key={s?.id || idx}
                            onClick={() => onSelect(s)}
                            className="w-full text-left border rounded-lg p-4 hover:shadow-sm transition-shadow bg-white"
                        >
                            <div className="font-semibold text-lg">{s?.name}</div>

                            <div className="text-sm text-gray-500">
                                {[s?.location, `ID: ${s?.id}`, s?.status].filter(Boolean).join(", ")}
                            </div>
                        </button>

                    ))}
                </div>
            </div>
        </div>
    );
}
